import React from "react";
import { shallowEqual } from "react-redux";
import type { MODAL_USC_BASKET_DETAILS } from "../../../utils/modalConstants";
import { useUscSelector } from "../../../redux/store";
import { getFeeListSelector, getServicesListSelector } from "../../../utils/externalPriceUtils";
import { useUscLabel, useUscOptionalLabel } from "../../../utils/labels";
import UscFinanceOptionsButton from "../common/basket/common/UscFinanceOptionsButton";
import UscAddInsuranceButton from "../common/basket/common/UscAddInsuranceButton";
import UscBasketCtas from "../common/basket/common/UscBasketCtas";
import UscFinanceToggle from "../common/basket/common/UscFinanceToggle";
import { type ModalViewType, EcoTagType } from "../../../../../common-deprecated/types/CommonTypes";
import DualCurrencyDisclaimer from "../../../../shared-components/toyota/dual-currency-disclaimer/DualCurrencyDisclaimer";
import UscCashPrice from "../common/price/UscCashPrice";
import BasketDisclaimers from "../common/basket/content/BasketDisclaimers";
import BasketServices from "../common/basket/content/BasketServices";
import UscBasketDetailsTotalPrice from "../common/basket/content/UscBasketDetailsTotalPrice";
import PdfButton from "../common/basket/common/PdfButton";
import UscMonthlyPriceDisclaimer from "../common/price/UscMonthlyPriceDisclaimer";
import { enableUscInsurance, enableUscMonthlyRate } from "../../../utils/settings";
import UscInsurancePanel from "../common/basket/UscInsurancePanel";
import UscBasketFees from "../common/basket/UscBasketFees";
import { getVehicleTitle, showFuelTypeTag } from "../../../../shared-logic/utils/uscUtils";
import ModalBaseView from "../../../../shared-components/toyota/styles/organisms/Modal";
import * as ModalStyles from "../../../../shared-components/toyota/styles/organisms/Modal.styles";
import * as Styles from "./UscBasketDetailsModal.styles";
import Icon from "../../../../shared-components/toyota/styles/atoms/Icon";
import Text from "../../../../shared-components/toyota/styles/atoms/Text";
import List from "../styles/atoms/List";
import SaveToMyToyotaButton from "../common/basket/common/SaveToMyToyotaButton";
import Sidebar from "../styles/atoms/Sidebar";
import Content from "../styles/atoms/Content";
import { UscTrackingLocation } from "../../../utils/tracking";
import { financeServicesSelector } from "../../../utils/financeUtils";
import { hasDualCurrency } from "../../../../../common-deprecated/settings/utils/financeSettingUtilsAem";
import { useCommonSelector } from "../../../../../common-deprecated/redux/commonStore";
import { showUscMonthlyDisclaimersSelector } from "../../../utils/disclaimerUtils";

export type UscBasketDetailsModalType = {
    type: typeof MODAL_USC_BASKET_DETAILS;
};

const UscBasketDetailsModal = (props: ModalViewType<UscBasketDetailsModalType>): JSX.Element | null => {
    const { show, close, modalSettings } = props;
    const settings = useCommonSelector((state) => state.commonSettings);
    const result = useUscSelector((state) => state.vehicle.result);
    const includedFeeList = useUscSelector(getFeeListSelector("included"), shallowEqual);
    const excludedFeeList = useUscSelector(getFeeListSelector("excluded"), shallowEqual);
    const enableInsurance = useUscSelector(({ vehicle, uscSettings, commonSettings }) =>
        vehicle.result ? enableUscInsurance(commonSettings, uscSettings, vehicle.result, vehicle.type) : false,
    );
    const insuranceIsActive = useUscSelector((state) => !!state.insurance.quote);
    const activeFinanceOption = useUscSelector((state) => state.basket.activeFinanceOption);
    const enableMonthly = useUscSelector(({ commonSettings, uscSettings, vehicle }) =>
        enableUscMonthlyRate(commonSettings, uscSettings, vehicle.result, vehicle.type),
    );
    const showMonthlyDisclaimers = useUscSelector(showUscMonthlyDisclaimersSelector, shallowEqual);
    const financeInfo = useUscSelector((state) => state.finance.info);
    const showFinanceTogglePrices = useUscSelector((state) => !state.uscSettings.hideFinanceTogglePrices);
    const monthlyLoading = useUscSelector((state) => state.finance.fetching || state.externalPrice.fetching);

    const [cashOptionLabel, monthlyOptionLabel, retailPriceLabel, titleAdditionalTaxesAndCostsLabel] = useUscLabel([
        "uscBasketOverviewFinanceOptionCash",
        "uscBasketOverviewFinanceOptionMonthly",
        "uscBasketConfigRetailPrice",
        "uscBasketTitleAdditionalTaxesAndCosts",
    ]);
    const selectedOptionLabel = useUscOptionalLabel("uscBasketOverviewFinanceOption");
    const financeServices = useUscSelector(financeServicesSelector, shallowEqual);
    const services = useUscSelector(getServicesListSelector("included"), shallowEqual);

    if (!result) return null;

    const vehicleTitle = getVehicleTitle(result.product);
    const { engine, versionName } = result.product;
    const { code: fuelTypeCode, description: fuelTypeName } = engine.marketingFuelType;
    const monthlyLabel = financeInfo?.productName || monthlyOptionLabel;

    const showActiveInsurance = enableInsurance && insuranceIsActive;
    const showExcludedFees = excludedFeeList.length > 0;
    const hasAdditionalCosts = showActiveInsurance || showExcludedFees;

    // The retail price section is hidden on monthly when the finance toggle prices are hidden
    const showRetailPriceSection = activeFinanceOption === "cash" || showFinanceTogglePrices;

    const servicesLength = financeServices.length + services.length;

    return (
        <ModalBaseView
            modalIdType={modalSettings.type}
            onClose={close}
            size="fullscreen"
            show={show}
            disableOutsideClick
            dialogLabelId="uscBasketDetailsTitle"
            dialogDescId="uscBasketDetailsDesc"
        >
            <ModalStyles.Header>
                <div>
                    <Text id="uscBasketDetailsTitle" tag="h2" variant="h6">
                        {vehicleTitle}
                    </Text>
                    <Styles.HeaderInfo id="uscBasketDetailsDesc">
                        {showFuelTypeTag(fuelTypeCode) && (
                            <Text tag="span" variant={EcoTagType.Hybrid}>
                                {fuelTypeName}
                            </Text>
                        )}
                        {!!versionName && (
                            <Text tag="span" variant="body-small-regular" color="grey-7">
                                {versionName}
                            </Text>
                        )}
                    </Styles.HeaderInfo>
                </div>
                <ModalStyles.Button type="button" onClick={close}>
                    <Icon variant="close" />
                </ModalStyles.Button>
            </ModalStyles.Header>
            <ModalStyles.Body noSpacing>
                <Styles.ContentWrapper>
                    <Content>
                        <Styles.Details>
                            <div>
                                <UscFinanceToggle trackingLocation={UscTrackingLocation.BasketDetails} />
                                {showMonthlyDisclaimers && <UscMonthlyPriceDisclaimer isPlaceholder={monthlyLoading} />}
                            </div>

                            {selectedOptionLabel && (
                                <List tag="ul" variant="basket">
                                    <li>
                                        <Text variant="body-small-book">{selectedOptionLabel}</Text>
                                        <Text variant="h7">
                                            {activeFinanceOption === "cash" ? cashOptionLabel : monthlyLabel}
                                        </Text>
                                    </li>
                                </List>
                            )}

                            {(!!servicesLength || showRetailPriceSection || hasDualCurrency(settings, false)) && (
                                <div>
                                    {showRetailPriceSection && (
                                        <Styles.RetailPriceList role="list">
                                            <li>
                                                <Text variant="h7">{retailPriceLabel}</Text>
                                                <Text variant="body-small-semibold">
                                                    <UscCashPrice showDiscountPrice showRetailPrice />
                                                </Text>
                                            </li>
                                        </Styles.RetailPriceList>
                                    )}
                                    <DualCurrencyDisclaimer />
                                    <BasketServices />
                                </div>
                            )}

                            {includedFeeList.length > 0 && <UscBasketFees feeType="included" />}
                            <UscBasketDetailsTotalPrice />
                            {hasAdditionalCosts && (
                                <div>
                                    <Text tag="h3" variant="h7" color="grey-7" spacing={{ mb: "spacing-1" }}>
                                        {titleAdditionalTaxesAndCostsLabel}
                                    </Text>
                                    {showActiveInsurance && (
                                        <UscInsurancePanel trackingLocation={UscTrackingLocation.BasketDetails} />
                                    )}
                                    {showExcludedFees && <UscBasketFees feeType="excluded" />}
                                </div>
                            )}
                            <BasketDisclaimers />
                        </Styles.Details>
                    </Content>
                    <Sidebar variant="basket-details">
                        <Styles.Actions>
                            <Styles.MobileOnly>
                                <Styles.CtaWrapper>
                                    {enableMonthly && (
                                        <UscFinanceOptionsButton trackingLocation={UscTrackingLocation.Footer} />
                                    )}
                                    {enableInsurance && (
                                        <UscAddInsuranceButton trackingLocation={UscTrackingLocation.Footer} />
                                    )}
                                </Styles.CtaWrapper>

                                <PdfButton buttonVariant="basket-cta" trackingLocation={UscTrackingLocation.Footer} />
                                <SaveToMyToyotaButton
                                    buttonVariant="basket-cta"
                                    trackingLocation={UscTrackingLocation.Footer}
                                />
                            </Styles.MobileOnly>
                            <Styles.DesktopOnly>
                                {(enableMonthly || enableInsurance) && (
                                    <Styles.CtaWrapper>
                                        {enableMonthly && (
                                            <UscFinanceOptionsButton
                                                trackingLocation={UscTrackingLocation.BasketDetails}
                                            />
                                        )}
                                        {enableInsurance && (
                                            <UscAddInsuranceButton
                                                trackingLocation={UscTrackingLocation.BasketDetails}
                                            />
                                        )}
                                    </Styles.CtaWrapper>
                                )}
                                <UscBasketCtas trackingLocation={UscTrackingLocation.BasketDetails} />

                                <Styles.SecondaryCtas>
                                    <PdfButton
                                        buttonVariant="basket-cta"
                                        trackingLocation={UscTrackingLocation.BasketDetails}
                                    />
                                    <SaveToMyToyotaButton
                                        buttonVariant="basket-cta"
                                        trackingLocation={UscTrackingLocation.BasketDetails}
                                    />
                                </Styles.SecondaryCtas>
                            </Styles.DesktopOnly>
                        </Styles.Actions>
                    </Sidebar>
                </Styles.ContentWrapper>
            </ModalStyles.Body>
        </ModalBaseView>
    );
};

export default UscBasketDetailsModal;
